<template>
  <div :class="{ 'container-fluid': showBrand }">
    <Brand v-if="showBrand" :width="110" :height="110" />
    <div class="p-4">
      <Spinner v-if="loading" />
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-else>
        <div class="mb-4">
          <Multiselect
            class="multiselect-violet"
            :close-on-select="true"
            :clear-on-select="false"
            :max="1"
            mode="single"
            :placeholder="t('Choose a date')"
            v-model="selectedDate"
            :options="dates"
            @change="loadNonAssociatedProjects"
          />
        </div>
        <div v-if="selectedDate" class="mb-4">
          <input
            type="text"
            class="form-control"
            v-model="searchQuery"
            :placeholder="t('Search by file name')"
            @input="currentPage = 1"
          />
        </div>
        <div v-if="filteredProjects.length">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th v-if="isManager">ID</th>
                <th>{{ t('Project') }}</th>
                <th v-if="isManager">{{ t('Software') }}</th>
                <th v-if="isManager">{{ t('Department') }}</th>
                <th v-if="isManager">{{ t('Prescription Code') }}</th>
                <th>{{ t('Actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in paginatedProjects" :key="project.key">
                <td v-if="isManager"><small>{{ project.id }}</small></td>
                <td><small :title="project.file_name">{{ truncate(project.file_name, 50) }}</small></td>
                <td v-if="isManager"><small>{{ project.software }}</small></td>
                <td v-if="isManager"><small>{{ project.department }}</small></td>
                <td v-if="isManager">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="project.numberText"
                    :placeholder="t('Enter the prescription code')"
                  />
                </td>
                <td>
                  <div class="d-flex align-items-center justify-content-center">
                    <button class="btn btn-sm btn-primary ms-2" @click="handleAssociate(project.id)">
                      {{ isManager ? t('Associate') : t('Associate to this test') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <Pagination
            v-if="totalPages > 1"
            :array="filteredProjects"
            :pageNumber="currentPage"
            :totalPages="totalPages"
            @increment="currentPage++"
            @decrement="currentPage--"
          />
        </div>
        <div v-else-if="selectedDate" class="alert alert-info">
          {{ t('No unassociated projects found for the selected date') }}
        </div>
        <div v-else class="alert alert-info">
          {{ t('Select a date to view unassociated projects') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Brand from '@/components/Brand';
import { ref, onMounted, computed, watch } from 'vue';
import { fetchProjects } from '@/use/repositories/projects/fetchProjects';
import { associateProject } from '@/use/repositories/projects/associateProject';
import Spinner from '@/components/general/Spinner.vue';
import Pagination from '@/components/general/Pagination.vue';
import Multiselect from '@vueform/multiselect';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { success } from '@/use/toast/success';
import { error as danger } from '@/use/toast/error';
import { formatDate } from '@/use/utilities/time/formatDate.js';

export default {
  name: 'NonAssociatedProjects',
  components: { Spinner, Pagination, Multiselect, Brand },
  props: {
    prescriptionTestId: { type: [Number, String], default: null },
    isManager: { type: Boolean, default: false },
    itemsPerPage: { type: Number, default: 10 },
    showBrand: { type: Boolean, default: false },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref(null);
    const dates = ref([]);
    const selectedDate = ref(null);
    const projects = ref([]);
    const currentPage = ref(1);
    const searchQuery = ref('');
    const { t } = useI18n();
    const route = useRoute();

    const effectivePrescriptionTestId = computed(() => {
      if (props.prescriptionTestId) return props.prescriptionTestId;
      return route.params.prescriptionTestId ? Number(route.params.prescriptionTestId) : null;
    });

    const transformProjects = (projects) =>
      projects.map((project) => ({
        ...project,
        key: `${project.file_name}-${project.created_at}`,
        numberText: project.numberText || '',
      }));

    const loadDates = async () => {
      loading.value = true;
      error.value = null;
      try {
        const { dates: fetchedDates } = await fetchProjects('non_associated', null, null, 1);
        dates.value = fetchedDates.map((date) => ({
          value: date,
          label: formatDate(date, { iso: false, dash: false }),
        }));
      } catch (err) {
        error.value = t('Error loading dates');
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const loadNonAssociatedProjects = async () => {
      if (!selectedDate.value) {
        projects.value = [];
        return;
      }
      loading.value = true;
      error.value = null;
      try {
        const { projects: fetchedProjects } = await fetchProjects(
          'non_associated',
          null,
          selectedDate.value,
          0
        );
        projects.value = transformProjects(fetchedProjects);
      } catch (err) {
        error.value = t('Error loading unassociated projects');
        console.error(err);
        danger(err);
      } finally {
        loading.value = false;
      }
      currentPage.value = 1;
    };

    const handleAssociate = async (projectId) => {
      loading.value = true;
      error.value = null;

      const project = projects.value.find((p) => p.id === projectId);

      if (!props.isManager && !effectivePrescriptionTestId.value) {
        danger(t('ID del test di prescrizione richiesto'));
        loading.value = false;
        return;
      }

      if (props.isManager && !project?.numberText?.trim()) {
        danger(t('Inserire un codice di prescrizione'));
        loading.value = false;
        return;
      }

      try {
        const { message } = await associateProject(
          projectId,
          props.isManager ? project.numberText : effectivePrescriptionTestId.value,
          props.isManager ? 'by_number_text' : 'by_prescription_test_id'
        );
        projects.value = projects.value.filter((p) => p.id !== projectId);
        success(t(message));
      } catch (err) {
        error.value = t('Error associating project');
        console.error(err);
        danger(err.message || t('Unknown error'));
      } finally {
        loading.value = false;
      }
    };

    const truncate = (str, num) =>
      str && str.length > num ? str.slice(0, num) + '...' : str;

    const filteredProjects = computed(() => {
      if (!searchQuery.value) return projects.value;
      return projects.value.filter((project) =>
        project.file_name.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const paginatedProjects = computed(() => {
      const start = (currentPage.value - 1) * props.itemsPerPage;
      return [...filteredProjects.value]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(start, start + props.itemsPerPage);
    });

    const totalPages = computed(() =>
      Math.ceil(filteredProjects.value.length / props.itemsPerPage) || 1
    );

    watch(selectedDate, (newDate) => {
      if (newDate) {
        loadNonAssociatedProjects();
      }
    });

    onMounted(() => loadDates());

    return {
      loading,
      error,
      dates,
      selectedDate,
      projects,
      currentPage,
      totalPages,
      paginatedProjects,
      filteredProjects,
      loadNonAssociatedProjects,
      truncate,
      handleAssociate,
      t,
      searchQuery,
    };
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/abstracts/_variables.scss';
.invert {
  filter: invert(1);
}
.multiselect-violet {
  --ms-tag-bg: $violet !important;
  --ms-tag-color: $white !important;
  --ms-ring-color: $violet !important;
}
</style>